<script setup lang="ts">
import lightHeader from '@/assets/login/light.webp'
import darkHeader from '@/assets/login/dark.webp'
import lightLogo from '@/assets/newLogo/Logo_full_brand_color-3.webp'
import darkLogo from '@/assets/newLogo/Logo_full_brand_color-2.webp'
import lightTop from '@/assets/login/top-light.avif'
import darkTop from '@/assets/login/top-dark.avif'
import lightTopPhone from '@/assets/login/top-light-phone.avif'
import darkTopPhone from '@/assets/login/top-dark-phone.avif'

const FooterNavAsync = defineAsyncComponent({
  loader: () => import('@/components/Layout/FooterNav.vue'),
  loadingComponent: import('@/components/Common/DLoading.vue'),
  delay: 500,
})
const userStore = useUserStore()
const { isLogin } = storeToRefs(userStore)
const router = useRouter()
const { t } = useI18n()
const isDark = useDark()
const showMenu = ref(false)
const menuList = computed(() => [
  {
    name: 'dFansAI',
    label: 'dFans AI',
  },
  {
    name: 'videoGuide',
    label: t('Footer.VideoGuide'),
  },
  {
    name: 'FAQ',
    label: t('route.FAQ'),
  },
  {
    name: 'Signin',
    label: t('Global.LogIn'),
  },
])

const menuList2 = computed(() => [
  {
    name: 'Signup',
    label: t('Global.SignUp'),
  },
  {
    name: 'dFansAI',
    label: 'dFans AI',
  },
  {
    name: 'FAQ',
    label: t('route.FAQ'),
  },
  {
    name: 'videoGuide',
    label: t('Footer.VideoGuide'),
  },
])

useDfansHead({
  title: () => 'Login - dFans',
  meta: [
    {
      name: 'description',
      content: () => 'Login dfans, Engage with your fans and discover new creators.',
    },
  ],
})

watch(isLogin, () => {
  if (isLogin.value) {
    router.replace({ name: 'Home' })
  }
})

const handleClick = (name: string) => {
  if (name === 'dFansAI') {
    window.open('https://dFans.ai', '_blank')
  } else {
    router.push({ name })
  }
}

onMounted(() => {
  const url_params: any = useUrlSearchParams('history')
  const params = useUrlSearchParams('hash')
  if (url_params.logout || params.logout) {
    userStore.LOGOUT()
    return
  }
  if (isLogin.value) {
    router.replace({ name: 'Home' })
  }
})

const toggleMenu = () => {
  showMenu.value = !showMenu.value
}
</script>

<template>
  <div class="w-full bg-brandC dark:bg-brandD">
    <div
      class="!backdrop-blur-lg w-full sm:h-180 b-b df-b relative overflow-hidden bg-white dark:bg-brand dark:sm:bg-brand sm:pl--10"
    >
      <div sm:w-270 sm:mx-auto h-full>
        <div class="h-full flex flex-col relative sm:ml-10">
          <div class="flex items-center sm:pr-10 sm:pl-0 sm:h-19 h-16 px-4 relative z-10">
            <img
              :src="isDark ? darkLogo : lightLogo"
              alt="dfans.xyz The decentralized creator's platform"
              class="sm:h-7.5 h-5.5"
            />
            <div class="hidden ml-auto sm:flex items-center gap-x-2">
              <div
                v-for="item in menuList"
                :key="item.name"
                class="hover:bg-brandB hover:bg-op-20 hover:rounded-full py-3 px-4"
                @click="handleClick(item.name)"
              >
                <div class="text-base typeface-robotoMedium text cursor-pointer">
                  {{ item.label }}
                </div>
              </div>
              <dfansButton
                type="default"
                :text="t('Global.SignUp')"
                class="h-12 !text-base px-1 ml-1"
                round
                @click="handleClick('Signup')"
              />
            </div>
            <div ml-auto flex items-center gap-x-3 sm:hidden>
              <div
                class="text-base typeface-robotoMedium cursor-pointer"
                @click="handleClick('Signin')"
              >
                {{ t('Global.LogIn') }}
              </div>
              <div
                class="flex w-11 h-11 rounded-full brand-gradient flex-center"
                @click="toggleMenu"
              >
                <div i-ph:list-bold class="w-5 h-5 dark:text-brand text-white" />
              </div>
            </div>
            <div overflow-hidden absolute top-16 right-0 z-10 w-full>
              <div
                class="w-full transition-all duration-200"
                :class="{ 'max-h-0 py-0': !showMenu, 'max-h-50 py-2': showMenu }"
              >
                <div
                  class="mx-4 bg-white shadow-box dark:bg-brandD dark:b dark:df-b !dark:shadow-none rounded-md p-2"
                >
                  <!-- 内容保持不变 -->
                  <div
                    v-for="item in menuList2"
                    :key="item.name"
                    class="hover:bg-brandB hover:bg-op-10 text-base cursor-pointer rounded-md h-9 my-1 px-2 flex items-center"
                    @click="handleClick(item.name)"
                  >
                    {{ item.label }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex-1 flex justify-between items-center">
            <div
              class="h-full flex justify-center flex-col items-center pt-6 sm:items-start sm:pt-0 relative z-1"
            >
              <img
                :src="isDark ? darkHeader : lightHeader"
                alt="dfans.xyz The decentralized creator's platform"
                class="h-9 sm:h-11 sm:w-initial w-initial"
              />
              <div class="text-base mt-3 text-brandB typeface-avenirRoman">
                {{ t('login.lovedByCreators') }}
              </div>
              <div
                class="typeface-avenirBlack sm:mt-6 sm:text-13 sm:leading-16 sm:w-127.5 sm:px-0 sm:text-left text-center px-5 mt-8 text-8.5 leading-10"
              >
                {{ t('login.easyCryptoEarnings') }}
              </div>
              <div
                class="typeface-avenirRoman text-base sm:text-lg mt-2 text-brandB sm:w-122.5 sm:px-0 sm:text-left text-center px-5"
              >
                {{ t('login.easyCryptoEarningsDesc') }}
              </div>
              <div class="flex sm:items-center sm:flex-row flex-col mt-10">
                <dfansButton
                  type="default"
                  :text="t('Global.BecomeCreator')"
                  class="h-12 sm:h-13 !text-base px-3.5 sm:mr-2 typeface-avenirHeavy"
                  round
                  @click="handleClick('BecomeCreatorIntro')"
                />
                <div class="sm:hidden text-sm sm:text-base mt-2 text-brandB typeface-avenirRoman">
                  {{ t('login.freeAndLessThanAMinute') }}
                </div>
                <div
                  class="text-center sm:py-3.5 sm:px-7 py-3 px-4 font-box cursor-pointer hover:bg-brandB hover:bg-op-10 hover:rounded-full"
                  @click="handleClick('Signup')"
                >
                  <div class="text-base typeface-avenirHeavy text">
                    {{ t('Btn.SignUpAsAFan') }}
                  </div>
                </div>
              </div>
              <div
                class="hidden sm:block text-sm sm:text-base mt-2 text-brandB typeface-avenirRoman"
              >
                {{ t('login.freeAndLessThanAMinute') }}
              </div>
              <img
                :src="isDark ? darkTopPhone : lightTopPhone"
                alt="dfans.xyz The decentralized creator's platform"
                class="sm:hidden w-full"
              />
            </div>
            <div class="hidden sm:flex h-full absolute bottom-0 right-0 justify-end items-end">
              <img
                :src="isDark ? darkTop : lightTop"
                alt="dfans.xyz The decentralized creator's platform"
                class="h-161"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <LoginPost />
    <LoginFaq />
    <FooterNavAsync />
  </div>
</template>

<style scoped lang="less">
.shadow-box {
  box-shadow: 0px 1px 10px 5px rgba(138, 150, 163, 0.3);
}
</style>
